import React from 'react';
import PropTypes from 'prop-types';
import { usePagination } from '../Hooks';
import { ListBuilder, Li, Ul } from './ListBuilder';

const PaginationProps = {
	className: PropTypes.string,
	nextLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	prevLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	immovable: PropTypes.bool,
	as: PropTypes.shape({
		Container: PropTypes.elementType,
		List: PropTypes.elementType,
	}),
};

const PaginationDefault = {
	nextLabel: 'Next',
	prevLabel: 'Prev',
	className: 'rt-pagination',
};

export const Pagination = ({
	as: { Container = Ul, List = Li } = {},
	className,
	nextLabel,
	prevLabel,
	immovable,
}) => {
	const { allowPagination, totalPages, page, setPage } = usePagination();
	if (!allowPagination) return null;
	const builderClick = (rowNumber) => (e) => {
		e.preventDefault();
		setPage(rowNumber);
		if (!immovable) return;
		const root = document.documentElement;
		const old = root.scrollHeight;
		setTimeout(
			() => (root.scrollTop = root.scrollTop + (root.scrollHeight - old)),
			0
		);
	};

	const pageFirstSideStart = page - 2;
	const pageLastSideStart = page + 1;
	const pageFirstSideStartIsMoreThanOne = pageFirstSideStart > 1;
	const pageLastSideEndIsLessThanTotalPage = page + 2 < totalPages;
	const pageIsOne = page === 1 || totalPages === 0;
	const pageIsTotalPage = page === totalPages || totalPages === 0;
	const totalPageIsLessThanTen = totalPages < 10;
	const addExcessInFirstSideStart = Math.max(0, page - (totalPages - 4));
	const addExcessInLastSideStart = Math.max(0, 5 - page);
	return (
		<Container className={className}>
			<List
				children={prevLabel}
				disabled={pageIsOne}
				onClick={builderClick(Math.max(1, page - 1))}
			/>
			{totalPageIsLessThanTen ? (
				<ListBuilder
					List={List}
					total={totalPages}
					start={1}
					currentPage={page}
					builderClick={builderClick}
				/>
			) : (
				<>
					{pageFirstSideStartIsMoreThanOne && (
						<>
							<List children={1} onClick={builderClick(1)} />
							{pageFirstSideStart > 2 && (
								<List
									children='...'
									title='Previous 5 Pages'
									onClick={builderClick(Math.max(2, page - 5))}
								/>
							)}
						</>
					)}
					{pageIsOne || (
						<ListBuilder
							List={List}
							total={page > 2 ? 2 + addExcessInFirstSideStart : 1}
							start={Math.max(
								1,
								pageFirstSideStart - addExcessInFirstSideStart
							)}
							builderClick={builderClick}
						/>
					)}
					<List children={page} active onClick={(e) => e.preventDefault()} />
					{pageIsTotalPage || (
						<ListBuilder
							List={List}
							total={page < totalPages - 1 ? 2 + addExcessInLastSideStart : 1}
							start={pageLastSideStart}
							builderClick={builderClick}
						/>
					)}
					{pageLastSideEndIsLessThanTotalPage && (
						<>
							{pageLastSideStart < totalPages - 2 && (
								<List
									children='...'
									title='Next 5 Pages'
									onClick={builderClick(Math.min(totalPages - 1, page + 5))}
								/>
							)}
							<List children={totalPages} onClick={builderClick(totalPages)} />
						</>
					)}
				</>
			)}
			<List
				children={nextLabel}
				disabled={pageIsTotalPage}
				onClick={builderClick(Math.min(totalPages, page + 1))}
			/>
		</Container>
	);
};

Pagination.propTypes = PaginationProps;

Pagination.defaultProps = PaginationDefault;
